import React from 'react';
import { ExternalLink, Shield } from 'lucide-react';
import { SponsorPublico } from '../types/congreso.types';

interface SponsorsSectionProps {
  sponsors: SponsorPublico[];
}

export const SponsorsSection: React.FC<SponsorsSectionProps> = ({ sponsors }) => {
  // Criterio de Resiliencia Estética (FR-10): Si no hay sponsors cargados, la sección no se renderiza.
  if (!sponsors || sponsors.length === 0) {
    return null;
  }

  // Agrupar por tipo / jerarquía
  const sponsorsPorTipo = sponsors.reduce<Record<string, SponsorPublico[]>>((acc, curr) => {
    const key = curr.tipoSponsorNombre || 'Auspiciantes';
    if (!acc[key]) acc[key] = [];
    acc[key].push(curr);
    return acc;
  }, {});

  return (
    <section id="sponsors" className="py-24 bg-slate-50 relative border-t border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-50 border border-brand-cyan/30 text-brand-navy text-xs font-bold uppercase tracking-wider mb-4">
            <Shield className="w-3.5 h-3.5 text-brand-cyan" />
            Alianzas Estratégicas
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-brand-navy tracking-tight">
            Nuestros Patrocinadores
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Empresas e instituciones líderes que impulsan el desarrollo y la excelencia del Congreso.
          </p>
        </div>

        {/* Bloques por Jerarquía de Sponsors */}
        <div className="space-y-12">
          {Object.entries(sponsorsPorTipo).map(([tipoNombre, items]) => (
            <div key={tipoNombre} className="text-center">
              <h3 className="text-xs sm:text-sm font-extrabold uppercase tracking-widest text-brand-navy mb-6 flex items-center justify-center gap-3">
                <span className="h-px w-12 bg-gradient-to-r from-transparent to-brand-cyan/60"></span>
                <span>{tipoNombre}</span>
                <span className="h-px w-12 bg-gradient-to-l from-transparent to-brand-cyan/60"></span>
              </h3>

              <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
                {items.map((sponsor) => {
                  const content = (
                    <div className="group relative p-6 rounded-2xl bg-white border border-slate-200 hover:border-brand-cyan hover:shadow-xl transition-all duration-300 hover:scale-105 shadow-sm flex items-center justify-center w-48 sm:w-60 h-28">
                      {sponsor.logoUrl ? (
                        <img
                          src={sponsor.logoUrl}
                          alt={sponsor.nombre}
                          className="max-h-16 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                        />
                      ) : (
                        <span className="text-sm font-bold text-slate-700 group-hover:text-brand-navy transition-colors text-center">
                          {sponsor.nombre}
                        </span>
                      )}
                      {sponsor.sitioWebUrl && (
                        <ExternalLink className="absolute top-3 right-3 w-3.5 h-3.5 text-slate-400 group-hover:text-brand-cyan opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </div>
                  );

                  return sponsor.sitioWebUrl ? (
                    <a
                      key={sponsor.idSponsor}
                      href={sponsor.sitioWebUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-pointer"
                      aria-label={`Visitar sitio web de ${sponsor.nombre}`}
                    >
                      {content}
                    </a>
                  ) : (
                    <div key={sponsor.idSponsor}>{content}</div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
